/**
 * Auto TDS on the order detail: "Generate TDS" and the generated versions.
 *
 *   GET  orders/{id}/tds   { result: { versions: [...] } }, ascending by version
 *   POST orders/{id}/tds   202 { result: { version } } queued; 409 = one is already
 *                          being generated (joined, not an error); 422/403 { message }
 *
 * The backend answers with HTTP 200 and the outcome in the body (sendApiResponse:
 * { success, message, errors, status_code }); only transport and auth failures reject.
 * So a refusal is read from the envelope first, then from an axios error. Before this,
 * the 422 "no style code" came back as a 200 without a version and the button polled
 * for a version that never appeared (preprod, 2026-11-02).
 *
 * One shared store per order, like the file stamps: the action bar and anything
 * else that shows the TDS read the same versions and the same "generating" flag.
 * Generating is a background job on the server; the UI polls every 3 s for at
 * most 3 minutes, stops after 5 failed requests in a row, stops when the
 * component goes away, and picks the poll up again when the page is opened
 * while a version is still queued or running.
 *
 * Presigned URLs expire after 15 minutes, so they are never cached across a
 * visit (every open refetches) and are opened verbatim: `downloadFile` appends
 * `?q=…`, which would break the signature.
 */
import { computed, onBeforeUnmount, reactive, watch, type ComputedRef, type Ref } from 'vue';

import { can } from '@/composables';
import { showToast } from '@/composables/swal';
import { http } from '@/interceptors/axiosRequestInterceptor';
import type { OrderTdsVersion } from '@/models/Tds';

import type { FileStamp } from './useOrderFileStamps';

/** No new permission: generating a TDS is editing the order's tech pack. */
export const TDS_PERMISSION = 'update-techpack';
export const TDS_POLL_MS = 3000;
export const TDS_POLL_TIMEOUT_MS = 180000;
export const TDS_MAX_FAILURES = 5;

const GENERATE_FAILED = 'The TDS could not be generated.';

export type TdsPollOutcome =
  | { kind: 'done'; version: OrderTdsVersion }
  | { kind: 'failed'; message: string }
  | { kind: 'timeout' }
  | { kind: 'lost' }
  | { kind: 'aborted' };

interface Entry {
  /** Ascending by version, as the server sends them. */
  versions: OrderTdsVersion[];
  /** A poll of ours is running for this order. */
  polling: boolean;
}

const store = reactive<Record<number, Entry>>({});
const inFlight = new Map<number, Promise<void>>();
const polls = new Map<number, Promise<TdsPollOutcome>>();

const entry = (orderId: number): Entry => {
  if (!store[orderId]) store[orderId] = { versions: [], polling: false };

  return store[orderId];
};

const isPending = (v: OrderTdsVersion): boolean => v.status === 'queued' || v.status === 'running';

interface Envelope {
  success?: boolean;
  message?: string;
  status_code?: number | string;
  errors?: Record<string, string[]>;
  result?: unknown;
}

const envelopeMessage = (d: Envelope | undefined): string =>
  (d?.errors ? Object.values(d.errors)[0]?.[0] : undefined) || d?.message || '';

/** The refusal in a 200 envelope (success: false), with its status, or null. */
const refusal = (d: unknown): { status: number; message: string } | null => {
  const env = d as Envelope | undefined;
  if (!env || env.success !== false) return null;

  return { status: Number(env.status_code) || 0, message: envelopeMessage(env) };
};

const errorMessage = (e: unknown, fallback: string): string =>
  envelopeMessage((e as { response?: { data?: Envelope } })?.response?.data) || fallback;

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** The order's TDS versions, ascending. Tolerates an empty or odd response. */
export async function fetchOrderTds(orderId: number): Promise<OrderTdsVersion[]> {
  const res = await http.get(`orders/${orderId}/tds`);
  const versions = (res?.data as { result?: { versions?: unknown } } | undefined)?.result?.versions;

  return Array.isArray(versions)
    ? [...(versions as OrderTdsVersion[])].sort((a, b) => a.version - b.version)
    : [];
}

/**
 * Ask for a new TDS. Resolves with the queued version (or the one already being
 * generated, `joined`, on a 409); rejects with the server's sentence otherwise.
 */
export async function requestOrderTds(
  orderId: number
): Promise<{ version: OrderTdsVersion | null; joined: boolean }> {
  let res: { data?: unknown } | undefined;
  try {
    res = await http.post(`orders/${orderId}/tds`);
  } catch (e) {
    const response = (e as { response?: { status?: number; data?: unknown } })?.response;
    if (response?.status === 409) {
      const version =
        (response.data as { result?: { version?: OrderTdsVersion } } | undefined)?.result
          ?.version ?? null;

      return { version, joined: true };
    }
    throw new Error(errorMessage(e, GENERATE_FAILED));
  }
  const data = res?.data as Envelope | undefined;
  const version = (data?.result as { version?: OrderTdsVersion } | undefined)?.version ?? null;
  const refused = refusal(data);
  if (refused) {
    if (refused.status === 409) return { version, joined: true };
    throw new Error(refused.message || GENERATE_FAILED);
  }

  return { version, joined: false };
}

/** Fetch (or refetch) one order's versions into the store; concurrent callers share one request. */
export function loadOrderTds(orderId: number): Promise<void> {
  const pending = inFlight.get(orderId);
  if (pending) return pending;
  const p = fetchOrderTds(orderId)
    .then((versions) => {
      entry(orderId).versions = versions;
    })
    .catch(() => {
      // A 403 or a hiccup: no versions shown, no toast; the bar is a hint here.
      entry(orderId);
    })
    .finally(() => inFlight.delete(orderId));
  inFlight.set(orderId, p);

  return p;
}

/**
 * Poll until `target` (a version number) is done or failed. Without a target,
 * the first version above `baseline` decides. One poll per order at a time.
 */
export function pollOrderTds(
  orderId: number,
  target: number | null,
  baseline: number,
  signal: { aborted: boolean }
): Promise<TdsPollOutcome> {
  const running = polls.get(orderId);
  if (running) return running;

  const run = async (): Promise<TdsPollOutcome> => {
    const started = Date.now();
    let failures = 0;
    entry(orderId).polling = true;
    try {
      for (;;) {
        await sleep(TDS_POLL_MS);
        if (signal.aborted) return { kind: 'aborted' };
        try {
          const versions = await fetchOrderTds(orderId);
          failures = 0;
          entry(orderId).versions = versions;
          const v =
            target !== null
              ? versions.find((x) => x.version === target)
              : [...versions].reverse().find((x) => x.version > baseline);
          if (v?.status === 'done') return { kind: 'done', version: v };
          if (v?.status === 'failed')
            return { kind: 'failed', message: v.error || GENERATE_FAILED };
        } catch {
          if (++failures >= TDS_MAX_FAILURES) return { kind: 'lost' };
        }
        if (signal.aborted) return { kind: 'aborted' };
        if (Date.now() - started >= TDS_POLL_TIMEOUT_MS) return { kind: 'timeout' };
      }
    } finally {
      entry(orderId).polling = false;
      polls.delete(orderId);
    }
  };
  const p = run();
  polls.set(orderId, p);

  return p;
}

/** One sentence per way a poll ends; nothing when the page was left. */
export function reportTdsOutcome(outcome: TdsPollOutcome): void {
  switch (outcome.kind) {
    case 'done':
      showToast(`TDS v${outcome.version.version} is ready.`, 'success');
      break;
    case 'failed':
      showToast(outcome.message, 'error');
      break;
    case 'timeout':
      showToast('The TDS is taking longer than expected. Check back in a minute.', 'warning');
      break;
    case 'lost':
      showToast(
        'Lost contact while the TDS was being generated. Reload the page to see whether it finished.',
        'error'
      );
      break;
    default:
      break;
  }
}

/** Test seam. */
export function resetOrderTds(): void {
  for (const k of Object.keys(store)) delete store[Number(k)];
  inFlight.clear();
  polls.clear();
}

/* ------------------------------------------------------------------ labels */

export const tdsLabel = (v: OrderTdsVersion): string => `TDS v${v.version}`;

/** Who asked for the version and when it was ready, shaped as a file stamp. */
export const tdsStamp = (v: OrderTdsVersion): FileStamp | null =>
  v.requested_by
    ? {
        by: v.requested_by,
        user_id: v.requested_by_id ?? null,
        at: v.finished_at ?? v.created_at,
        action_type: 'tds_generated',
      }
    : null;

const dateTime = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** "Download TDS v2 — generated by Sara Lund, 28 Oct 2026, 13:01 · 1 warning: …" */
export const tdsTitle = (v: OrderTdsVersion): string => {
  const at = v.finished_at ?? v.created_at;
  const d = at ? new Date(at) : null;
  const when = d && !Number.isNaN(d.getTime()) ? `, ${dateTime.format(d)}` : '';
  const who = v.requested_by ? ` — generated by ${v.requested_by}${when}` : '';
  const n = v.warnings?.length ?? 0;
  const warn = n ? ` · ${n} warning${n === 1 ? '' : 's'}: ${v.warnings.join('; ')}` : '';

  return `Download ${tdsLabel(v)}${who}${warn}`;
};

/* ------------------------------------------------------------- composable */

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useOrderTds(orderId: Ref<number | null | undefined>) {
  let signal = { aborted: false };

  const canGenerate = computed(() => Boolean(can(TDS_PERMISSION)));
  const canView = computed(() => Boolean(can(TDS_PERMISSION) || can('view-merchant-pdf')));

  const all: ComputedRef<OrderTdsVersion[]> = computed(() =>
    orderId.value ? (store[orderId.value]?.versions ?? []) : []
  );
  /** The versions that can be opened, newest first. */
  const versions: ComputedRef<OrderTdsVersion[]> = computed(() =>
    all.value.filter((v) => v.status === 'done').reverse()
  );
  const latest = computed<OrderTdsVersion | null>(() => versions.value[0] ?? null);
  const generating = computed<boolean>(() =>
    Boolean(orderId.value && (store[orderId.value]?.polling || all.value.some((v) => isPending(v))))
  );

  const highestVersion = (id: number): number =>
    (store[id]?.versions ?? []).reduce((max, v) => Math.max(max, v.version), 0);

  const follow = async (id: number, target: number | null, baseline: number): Promise<void> => {
    reportTdsOutcome(await pollOrderTds(id, target, baseline, signal));
  };

  /** Load, and pick the poll up again when a version is still being generated. */
  const load = async (id: number): Promise<void> => {
    await loadOrderTds(id);
    if (signal.aborted || polls.has(id) || orderId.value !== id) return;
    const pending = (store[id]?.versions ?? []).filter(isPending);
    if (pending.length) {
      const target = pending[pending.length - 1].version;
      await follow(id, target, target - 1);
    }
  };

  watch(
    orderId,
    (id, prev) => {
      if (prev && prev !== id) {
        signal.aborted = true;
        signal = { aborted: false };
      }
      if (id && canView.value) void load(id);
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    signal.aborted = true;
  });

  const generate = async (): Promise<void> => {
    const id = orderId.value;
    if (!id || generating.value || !canGenerate.value) return;
    const baseline = highestVersion(id);
    entry(id).polling = true;
    showToast('Generating the TDS. This takes about a minute; you can keep working.', 'info');
    let target: number | null = null;
    try {
      const r = await requestOrderTds(id);
      target = r.version?.version ?? null;
      if (r.version) {
        const list = entry(id).versions.filter((v) => v.version !== r.version!.version);
        entry(id).versions = [...list, r.version].sort((a, b) => a.version - b.version);
      }
    } catch (e) {
      entry(id).polling = false;
      showToast((e as Error).message || GENERATE_FAILED, 'error');

      return;
    }
    // Still "generating": the poll takes the flag over (and clears it when it ends).
    await follow(id, target, baseline);
  };

  const openVersion = (v: OrderTdsVersion | null | undefined): void => {
    if (!v) return;
    if (!v.url) {
      showToast("The file isn't ready yet. Try again in a few minutes.", 'info');

      return;
    }
    window.open(v.url, '_blank', 'noopener');
  };

  const refresh = (): Promise<void> =>
    orderId.value ? loadOrderTds(orderId.value) : Promise.resolve();

  return { versions, latest, generating, canGenerate, canView, generate, openVersion, refresh };
}
